import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { Mail, KeyRound, Lock, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";

export default function ForgotPassword() {
  const { t, formatApiError } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 = email, 2 = OTP + new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [fallbackOtp, setFallbackOtp] = useState(null);

  const sendOtp = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
      if (data.otp) {
        setFallbackOtp(data.otp);
      }
      setStep(2);
    } catch (er) {
      setErr(formatApiError(er));
    } finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        email, otp, new_password: newPassword,
      });
      setMsg(data.message);
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch (er) {
      setErr(formatApiError(er));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="forgot-password-screen" className="min-h-screen bg-surface-soft flex items-center justify-center px-4 py-12">
      {/* Subtle background grid */}
      <div className="fixed inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />

      <div className="relative w-full max-w-[400px]">
        <div className="bg-white rounded-2xl border border-surface-border shadow-card-lg p-8">

          <Link to="/login" className="inline-flex items-center gap-1.5 text-[13px] text-ink-soft hover:text-ink font-medium mb-6 transition-colors">
            <ArrowLeft size={14} /> {t.back || "Back to login"}
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
              <ShieldCheck size={22} />
            </div>
            <div>
              <h1 className="text-[20px] font-heading font-extrabold text-ink">{t.forgotPassword || "Reset Password"}</h1>
              <p className="text-[12px] text-ink-soft font-body">{t.forgotPasswordDesc || "We'll send an OTP to your email"}</p>
            </div>
          </div>

          {step === 1 && (
            <form onSubmit={sendOtp} className="space-y-4">
              <label className="block">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-ink-muted font-body">{t.email}</span>
                <div className="mt-1.5 relative flex items-center border border-surface-border rounded-xl px-3.5 py-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/10 transition-all bg-surface-soft">
                  <Mail size={15} className="text-ink-muted mr-2.5 shrink-0" />
                  <input
                    data-testid="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 text-[14px] bg-transparent outline-none text-ink placeholder:text-ink-subtle font-body"
                  />
                </div>
              </label>

              <p className="text-[12px] text-ink-soft bg-brand-50 border border-brand-100 rounded-xl px-3.5 py-2.5 font-body">
                {t.otpTelegramHint || "Enter your registered email address. You will receive a 6 digit OTP."}
              </p>

              {err && <div className="text-[13px] text-white bg-red-500 rounded-xl px-3.5 py-2.5 font-semibold font-body">{err}</div>}

              <button
                data-testid="forgot-submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-brand-600 text-white text-[14px] font-semibold hover:bg-brand-700 active:scale-[0.99] transition-all shadow-brand disabled:opacity-60 font-body cursor-pointer"
              >
                {loading ? (t.loading || "Loading…") : (t.sendOtp || "Send OTP")}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={resetPassword} className="space-y-4">
              {msg && (
                <div className="text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3.5 py-2.5 font-semibold flex items-center gap-2 font-body">
                  <CheckCircle2 size={16} /> {msg}
                </div>
              )}

              {fallbackOtp && (
                <div className="text-[13px] text-ink-mid bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5 font-mono">
                  Email failed. Your debug OTP: <strong>{fallbackOtp}</strong>
                </div>
              )}

              <label className="block">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-ink-muted font-body">{t.otpCode || "OTP Code"}</span>
                <div className="mt-1.5 relative flex items-center border border-surface-border rounded-xl px-3.5 py-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/10 transition-all bg-surface-soft">
                  <KeyRound size={15} className="text-ink-muted mr-2.5 shrink-0" />
                  <input
                    data-testid="forgot-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="000000"
                    className="flex-1 text-[14px] bg-transparent outline-none text-ink font-mono tracking-widest text-center placeholder:text-ink-subtle"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-ink-muted font-body">{t.newPassword || "New Password"}</span>
                <div className="mt-1.5 relative flex items-center border border-surface-border rounded-xl px-3.5 py-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/10 transition-all bg-surface-soft">
                  <Lock size={15} className="text-ink-muted mr-2.5 shrink-0" />
                  <input
                    data-testid="forgot-new-pw"
                    type="password"
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••"
                    className="flex-1 text-[14px] bg-transparent outline-none text-ink placeholder:text-ink-subtle font-body"
                  />
                </div>
              </label>

              {err && <div className="text-[13px] text-white bg-red-500 rounded-xl px-3.5 py-2.5 font-semibold font-body">{err}</div>}

              <button
                data-testid="forgot-reset"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-brand-600 text-white text-[14px] font-semibold hover:bg-brand-700 active:scale-[0.99] transition-all shadow-brand disabled:opacity-60 font-body cursor-pointer"
              >
                {loading ? (t.loading || "Loading…") : (t.resetPassword || "Reset Password")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
